// ./src/components/features/CreateEventWizard/steps/PoisStep.tsx
//
// "What is nearby?" — shown when the Places nearby feature is on (ROADMAP A2b #62 P6(f)). Each place is made in the Places
// page's own `PoiEditDrawer` (category, address on a map, price, links), under a provider scoped by the event — the drawer
// saves THROUGH that provider and does nothing without it.
// ⓘ Before the Event page step, which offers the "Places to visit" block once a place exists.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Stack, Text } from '@mantine/core';
import { OpResult } from '@sdflc/api-helpers';
import { useTranslation } from 'react-i18next';

import { PointsOfInterestProvider, usePointsOfInterestContext } from '@/contexts';
import { useToast } from '@/hooks';
import { PointOfInterest, WHOLE_SET } from '@/utils/api';
import { ConfirmModal } from '@/components/generic';
import { PoiEditDrawer } from '@/components/features/Pois';
import { refusalReason } from '@/utils';

import { useEventWizard } from '../WizardContext';
import { WizardItemRow } from '../components/WizardItemRow';
import { WizardListSection } from '../components/WizardListSection';

/** The host's order, then by name — as on the Places page. */
const byOrderThenName = (a: PointOfInterest, b: PointOfInterest): number =>
  (a.orderNo ?? 0) - (b.orderNo ?? 0) || (a.name ?? '').localeCompare(b.name ?? '');

const PoisStepBody: React.FC<{ eventId: string }> = ({ eventId }) => {
  const { t } = useTranslation(['events', 'common']);
  const toast = useToast();
  const { saving } = useEventWizard();
  const { pois, list, isLoaded, remove } = usePointsOfInterestContext();

  const [readError, setReadError] = useState<OpResult | null>(null);
  const [drawer, setDrawer] = useState<{ recordId: string | null } | null>(null);
  const [pendingRemove, setPendingRemove] = useState<PointOfInterest | null>(null);
  const [removing, setRemoving] = useState(false);

  const load = useCallback(async () => {
    const res = await list({ eventId: [eventId] }, { page: 1, pageSize: WHOLE_SET }, undefined, true);

    setReadError(res.didSucceed() ? null : res);
  }, [list, eventId]);

  useEffect(() => {
    void load();
  }, [load]);

  const sorted = useMemo(() => [...pois].sort(byOrderThenName), [pois]);

  const afterSave = () => {
    setDrawer(null);
    void load();
  };

  const confirmRemove = async () => {
    if (!pendingRemove?.id) {
      return;
    }

    setRemoving(true);

    const res = await remove(pendingRemove.id);

    setRemoving(false);

    if (!res.didSucceed()) {
      toast.error(refusalReason(res));

      return;
    }

    setPendingRemove(null);
    void load();
  };

  return (
    <Stack gap="lg">
      <Text size="md" c="dimmed">
        {t('events:wizard.pois.intro', {
          defaultValue:
            'Point your guests to what is around — parking, places to eat, things to see. The Event page step offers a “Places to visit” block to show them.',
        })}
      </Text>

      <WizardListSection
        title={t('events:wizard.pois.title', { defaultValue: 'Places nearby' })}
        addLabel={t('events:wizard.pois.add', { defaultValue: 'Add a place' })}
        onAdd={() => setDrawer({ recordId: null })}
        gate={null}
        gateWhat=""
        disabled={saving}
        isLoaded={isLoaded || !!readError}
        readError={readError}
        onRetry={() => void load()}
        errorTitle={t('events:wizard.pois.loadFailed', { defaultValue: 'Could not load the places' })}
        operation="pointOfInterestList"
        emptyText={t('events:wizard.pois.none', { defaultValue: 'No places yet.' })}
        isEmpty={sorted.length === 0}
      >
        {sorted.map((p) => (
          <WizardItemRow
            key={p.id}
            icon="MapPin"
            title={p.name ?? ''}
            subtitle={p.streetAddress || null}
            disabled={saving}
            onEdit={() => setDrawer({ recordId: p.id as string })}
            onRemove={() => setPendingRemove(p)}
          />
        ))}
      </WizardListSection>

      <PoiEditDrawer
        opened={!!drawer}
        onClose={() => setDrawer(null)}
        recordId={drawer?.recordId ?? null}
        eventId={eventId}
        onCreated={afterSave}
        onUpdated={afterSave}
      />

      <ConfirmModal
        opened={!!pendingRemove}
        onClose={() => setPendingRemove(null)}
        onConfirm={() => void confirmRemove()}
        type="danger"
        confirmInProgress={removing}
        title={t('events:wizard.pois.removeTitle', { defaultValue: 'Remove this place?' })}
        message={t('events:wizard.tickets.removeMessage', { defaultValue: '“{{name}}” will be removed from this event.', name: pendingRemove?.name ?? '' })}
        confirmLabel={t('common:actions.remove', { defaultValue: 'Remove' })}
      />
    </Stack>
  );
};

export const PoisStep: React.FC = () => {
  const { t } = useTranslation(['events']);
  const { createdEventId } = useEventWizard();

  if (!createdEventId) {
    return (
      <Text size="md" c="dimmed">
        {t('events:wizard.tickets.noEvent', { defaultValue: 'Your event is created at the end of the Guests step.' })}
      </Text>
    );
  }

  return (
    <PointsOfInterestProvider initialFilters={{ eventId: [createdEventId] }} pageSize={WHOLE_SET} autoLoad={false}>
      <PoisStepBody eventId={createdEventId} />
    </PointsOfInterestProvider>
  );
};
